import {Box, HBox} from '@buncha/components/core/view';
import {H5Text, H6Text} from '@buncha/components/core/text';
import {Linking, Platform, StyleSheet} from 'react-native';
import {
  PERMISSIONS,
  check,
  checkLocationAccuracy,
} from 'react-native-permissions';
import React, {ReactNode, useCallback, useEffect} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import AppModal from '@buncha/common/components/CustomModal/AppModal';
import {HomeScreens} from '@buncha/home/navigation/types';
import LocationIcon from '@buncha/components/core/icon/LocationIcon';
import LocationTick from '@buncha/components/core/icon/LocationTick';
import {Modules} from '@buncha/utils/types';
import SettingsIcon from '@buncha/components/core/icon/SettingsIcon';
import SwitchIcon from '@buncha/components/core/icon/SwitchIcon';
import font from '@buncha/appStyles/style/font';
import {useAppState} from '@buncha/config/hooks/appState';
import {useGetLocale} from '@buncha/localization/hooks';

interface LocationPermissionModalProps {
  open: boolean;
  onClose: () => void;
}

const LocationPermissionModal = (props: LocationPermissionModalProps) => {
  const theme = useColorThemeType('main');
  const muliFont = useFontTheme('muli');
  const appState = useAppState();
  const {title, body, button} =
    useGetLocale(Modules.aboutShift, HomeScreens.AboutShift) ?? {};

  const openSettings = useCallback(() => {
    Linking.openSettings();
  }, []);

  const cell = useCallback(
    (header: string, subHeader: string, icon: ReactNode) => {
      return (
        <HBox style={styles.cellBody}>
          {icon}
          <Box>
            <H5Text
              style={[
                styles.textAlignLeft,
                {
                  fontFamily: muliFont[700].normal,
                  color: theme?.text.blue[300],
                },
              ]}>
              {header}
            </H5Text>
            <H6Text style={[styles.textAlignLeft]}>{subHeader}</H6Text>
          </Box>
        </HBox>
      );
    },
    [muliFont, theme?.text.blue],
  );

  useEffect(() => {
    if (appState !== 'active') return;

    const access = Platform.select({
      android: PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION,
      ios: PERMISSIONS.IOS.LOCATION_ALWAYS,
    });
    if (!access) return;
    check(access).then(status => {
      if (status === 'granted')
        if (Platform.OS === 'ios')
          checkLocationAccuracy().then(accuracy => {
            if (accuracy === 'full') props.onClose();
          });
        else props.onClose();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appState]);

  return (
    <AppModal
      isOpen={props?.open}
      onClose={props?.onClose}
      topCTAHandelr={openSettings}
      bottomCTAHandelr={props?.onClose}
      hideBottomCta={false}
      modalBody={
        <Box style={styles.modalBody}>
          {cell(
            body?.locationSettings?.openSettings,
            body?.locationSettings?.step1,
            <SettingsIcon
              fill={theme?.icon.gray[100]}
              style={styles.settingsIcon}
            />,
          )}
          {cell(
            body?.locationSettings?.tapLocation,
            body?.locationSettings?.step2,
            <LocationIcon
              fill={theme?.icon.gray[100]}
              style={styles.locationIcon}
            />,
          )}
          {cell(
            body?.locationSettings?.enableAccess,
            body?.locationSettings?.step3,
            <LocationTick
              fill={theme?.icon.gray[100]}
              style={styles.locationAllowIcon}
            />,
          )}
          {cell(
            body?.locationSettings?.preciseLocation,
            body?.locationSettings?.step4,
            <SwitchIcon
              fill={theme?.icon.gray[100]}
              style={styles.locationAllowIcon}
            />,
          )}
        </Box>
      }
      title={title?.backgroundLocations}
      subTitle={body?.onfleetRequireslocation}
      titleStyle={styles.headerTypo}
      ctaBottomTitle={button?.cancel}
      ctaTopTitle={button?.openSettings}
      topCTAColor={theme?.background.green[100]}
    />
  );
};

export default React.memo(LocationPermissionModal);

const styles = StyleSheet.create({
  headerTypo: {
    fontFamily: font.fonts['Mortise-Bold'],
    ...font.FontSize.fontH2,
  },
  modalBody: {
    alignSelf: 'center',
    gap: 10,
  },
  cellBody: {
    justifyContent: 'flex-start',
    alignSelf: 'flex-start',
  },
  textAlignLeft: {
    textAlign: 'left',
  },
  settingsIcon: {marginRight: 5},
  locationIcon: {marginRight: 0, marginLeft: 5},
  locationAllowIcon: {marginRight: 10},
});
